"use client";

import Image, { StaticImageData } from "next/image";
import { memo } from "react";
import { useLocale } from "next-intl";
import { useRouter, usePathname } from "@/i18n/navigation";
import { useSearchParams } from "next/navigation";
import CustomSelect from "@/components/CustomSelect";
import { cn } from "@/lib/utils";
import ukFlagIcon from "@/assets/icons/header/language-uk.svg";
import cambodiaFlagIcon from "@/assets/icons/header/language-khmer.svg";
import chineseFlagIcon from "@/assets/icons/header/language-chinese.svg";

export type LanguageValue = "en" | "kh" | "zh";
export type LanguageOption = {
  value: LanguageValue;
  label: string;
  icon: StaticImageData;
};

export const LANGUAGE_OPTIONS: LanguageOption[] = [
  {
    value: "en",
    label: "English (UK)",
    icon: ukFlagIcon,
  },
  {
    value: "kh",
    label: "Khmer",
    icon: cambodiaFlagIcon,
  },
  {
    value: "zh",
    label: "Chinese",
    icon: chineseFlagIcon,
  },
];

type LanguageComboboxProps = {
  showTriggerLabel?: boolean;
  showIcon?: boolean;
  triggerClassName?: string;
  contentClassName?: string;
  itemClassName?: string;
};

const LanguageCombobox = ({
  showTriggerLabel = false,
  showIcon = false,
  triggerClassName,
  contentClassName,
  itemClassName,
}: LanguageComboboxProps) => {
  const locale = useLocale() as LanguageValue;
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const handleLanguageChange = (value: LanguageValue) => {
    const queryString = searchParams.toString();
    const targetPath = queryString ? `${pathname}?${queryString}` : pathname;
    router.replace(targetPath, { locale: value });
  };

  return (
    <CustomSelect<LanguageValue, LanguageOption>
      options={LANGUAGE_OPTIONS}
      value={locale}
      onValueChange={handleLanguageChange}
      ariaLabel="Select language"
      showIcon={showIcon}
      styles={{
        trigger: cn(
          "h-[22px] w-auto min-w-0 border-0 bg-transparent px-0 py-0 shadow-none hover:bg-transparent focus:border-transparent",
          triggerClassName,
        ),
        content: cn("w-[241px] rounded-[16px]", contentClassName),
        item: cn("h-[56px] px-6", itemClassName),
      }}
      renderTriggerValue={(option) =>
        option ? (
          showTriggerLabel ? (
            <span className="truncate text-base font-semibold text-[#1C1E21]">
              {option.label}
            </span>
          ) : (
            <Image
              src={option.icon}
              alt={option.label}
              width={22}
              height={22}
              priority
              sizes="22px"
              className="h-[22px] w-[22px]"
            />
          )
        ) : null
      }
      renderItem={(option) => (
        <div className="flex w-full items-center gap-4 pr-6">
          <Image
            src={option.icon}
            alt=""
            width={22}
            height={22}
            className="h-[22px] w-[22px]"
          />
          <span className="truncate text-[16px] font-medium leading-6 text-[#2D2D2D]">
            {option.label}
          </span>
        </div>
      )}
    />
  );
};

export default memo(LanguageCombobox);
